import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
import { PublicRoute } from './PublicRoute';
import { AppLayout } from '@/components/layout/AppLayout';
import { UserRole } from '@/types';
import { useAuth } from '@/hooks/useAuth';
import { PageLoader } from '@/components/common/LoadingSpinner';

const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const UnderReviewPage = lazy(() => import('@/pages/auth/UnderReviewPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

const ClubDashboardPage = lazy(() => import('@/pages/club/ClubDashboardPage'));
const ClubMatchesPage = lazy(() => import('@/pages/club/ClubMatchesPage'));
const ClubCreateMatchPage = lazy(() => import('@/pages/club/ClubCreateMatchPage'));
const ClubProfilePage = lazy(() => import('@/pages/club/ClubProfilePage'));
const ClubBrowseNgoPage = lazy(() => import('@/pages/club/ClubBrowseNgoPage'));

const NgoDashboardPage = lazy(() => import('@/pages/ngo/NgoDashboardPage'));
const NgoPledgesPage = lazy(() => import('@/pages/ngo/NgoPledgesPage'));
const NgoPledgeDetailPage = lazy(() => import('@/pages/ngo/NgoPledgeDetailPage'));
const NgoProfilePage = lazy(() => import('@/pages/ngo/NgoProfilePage'));
const NgoSupportingClubsPage = lazy(() => import('@/pages/ngo/NgoSupportingClubsPage'));

const PlayerDashboardPage = lazy(() => import('@/pages/player/PlayerDashboardPage'));
const PlayerMatchesPage = lazy(() => import('@/pages/player/PlayerMatchesPage'));
const PlayerCreateMatchPage = lazy(() => import('@/pages/player/PlayerCreateMatchPage'));
const PlayerBrowseNgoPage = lazy(() => import('@/pages/player/PlayerBrowseNgoPage'));
const PlayerProfilePage = lazy(() => import('@/pages/player/PlayerProfilePage'));

const AdminDashboardPage = lazy(() => import('@/pages/admin/AdminDashboardPage'));
const AdminMatchVerificationPage = lazy(() => import('@/pages/admin/AdminMatchVerificationPage'));
const AdminApprovalsPage = lazy(() => import('@/pages/admin/AdminApprovalsPage'));

const AdminClubsPage = lazy(() => import('@/pages/admin/AdminClubsPage'));
const AdminNgosPage = lazy(() => import('@/pages/admin/AdminNgosPage'));
const AdminPlayersPage = lazy(() => import('@/pages/admin/AdminPlayersPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/AdminSettingsPage'));

function LoadingFallback() {
  return <PageLoader />;
}

function RootRedirect() {
  const { user, loading, logout } = useAuth();

  if (loading) return <LoadingFallback />;

  if (!user) return <Navigate to="/login" replace />;

  if (user.role !== UserRole.ADMIN && (user.status === 'suspended' || user.status === 'rejected')) {
    logout();
    return <Navigate to="/login" replace />;
  }

  if (user.role !== UserRole.ADMIN && user.status !== 'active') {
    return <Navigate to="/under-review" replace />;
  }

  switch (user.role) {
    case UserRole.ADMIN:
      return <Navigate to="/admin/dashboard" replace />;
    case UserRole.CLUB:
      return <Navigate to="/club/dashboard" replace />;
    case UserRole.NGO:
      return <Navigate to="/ngo/dashboard" replace />;
    case UserRole.PLAYER:
      return <Navigate to="/player/dashboard" replace />;
    default:
      return <Navigate to="/login" replace />;
  }
}

const router = createBrowserRouter([
  {
    path: '/',
    element: <RootRedirect />,
  },

  {
    path: '/login',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      </Suspense>
    ),
  },
  {
    path: '/register/:type',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      </Suspense>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PublicRoute>
          <ForgotPasswordPage />
        </PublicRoute>
      </Suspense>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <PublicRoute>
          <ResetPasswordPage />
        </PublicRoute>
      </Suspense>
    ),
  },
  {
    path: '/under-review',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <UnderReviewPage />
      </Suspense>
    ),
  },

  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      {
        path: '/club/dashboard',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.CLUB, UserRole.ADMIN]}>
              <ClubDashboardPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/club/matches',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.CLUB, UserRole.ADMIN]}>
              <ClubMatchesPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/club/matches/create',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.CLUB, UserRole.ADMIN]}>
              <ClubCreateMatchPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/club/profile',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.CLUB, UserRole.ADMIN]}>
              <ClubProfilePage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/club/browse-ngo',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.CLUB, UserRole.ADMIN]}>
              <ClubBrowseNgoPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: '/player/dashboard',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.PLAYER, UserRole.ADMIN]}>
              <PlayerDashboardPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: '/player/matches',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.PLAYER, UserRole.ADMIN]}>
              <PlayerMatchesPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: '/player/profile',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.PLAYER, UserRole.ADMIN]}>
              <PlayerProfilePage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/player/browse-ngo',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.PLAYER, UserRole.ADMIN]}>
              <PlayerBrowseNgoPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/player/matches/create',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.PLAYER, UserRole.ADMIN]}>
              <PlayerCreateMatchPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: '/ngo/dashboard',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.NGO, UserRole.ADMIN]}>
              <NgoDashboardPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/ngo/clubs',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.NGO, UserRole.ADMIN]}>
              <NgoSupportingClubsPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/ngo/pledges',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.NGO, UserRole.ADMIN]}>
              <NgoPledgesPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/ngo/pledges/:id',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.NGO, UserRole.ADMIN]}>
              <NgoPledgeDetailPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/ngo/profile',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.NGO, UserRole.ADMIN]}>
              <NgoProfilePage />
            </ProtectedRoute>
          </Suspense>
        ),
      },

      {
        path: '/admin/dashboard',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminDashboardPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/match-verification',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminMatchVerificationPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/approval',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminApprovalsPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/clubs',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminClubsPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/ngos',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminNgosPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/players',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminPlayersPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/admin/settings',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <ProtectedRoute allowedRoles={[UserRole.ADMIN]}>
              <AdminSettingsPage />
            </ProtectedRoute>
          </Suspense>
        ),
      },
      {
        path: '/dashboard',
        element: <RootRedirect />,
      },
    ],
  },

  {
    path: '*',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
